import { createSimpleMutator } from '@superbet-group/web.lib.vue-utilities';
import { Commit } from 'vuex';

import {
    getPromotions,
    getPlayerPromotions,
    promotionOptIn,
} from '@app/promotionHub/promotions/services/PromotionService';

import { PromotionState } from './types';
import {
    Promotion,
    PromotionStatus,
} from '@src/app/promotionHub/promotions/types/Promotion';
import { errorGuard } from '@core/utils/ErrorGuard';
import {
    adjustWelcomeBonusPromotions,
    fillPromotionsWithBonusData,
    sortAndGroupPromotions,
    sortPromotions,
} from './helpers';
import { getConfig } from '@superbet-group/web.lib.config';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { PromotionHubTab } from '@src/app/promotionHub/shared/types/promotionHubTab';
import PromotionOffers from '@app/promotionHub/promotions/components/PromotionOffers.vue';
import { PromotionHubQuickFilter } from '@src/app/promotionHub/shared/utils/promotionHubQuickFilters';

const SET_AVAILABLE_PROMOTIONS = 'SET_AVAILABLE_PROMOTIONS';
const SET_IN_PROGRESS_PROMOTIONS = 'SET_IN_PROGRESS_PROMOTIONS';
const SET_HISTORY_PROMOTIONS = 'SET_HISTORY_PROMOTIONS';
const SET_EXPIRED_PROMOTIONS = 'SET_EXPIRED_PROMOTIONS';
const SET_ARE_PROMOTIONS_FETCHED = 'SET_ARE_PROMOTIONS_FETCHED';
const SET_IS_PROMOTION_OPTIN_PROCESSING = 'SET_IS_PROMOTION_OPTIN_PROCESSING';
const SET_PROMOTION_HUB_ACTIVE_TAB = 'SET_PROMOTION_HUB_ACTIVE_TAB';
const SET_PROMOTION_HUB_ACTIVE_QUICK_FILTER = 'SET_PROMOTION_HUB_ACTIVE_QUICK_FILTER';

const bonusProvider = getConfig().countrySpecific.user.bonus.bonusProvider;

function defaultState(): PromotionState {
    return {
        availablePromotions: [],
        inProgressPromotions: [],
        historyPromotions: [],
        expiredPromotions: [],
        arePromotionsFetched: false,
        isPromotionOptInProcessing: false,
        promotionHubActiveTab: {
            value: PromotionHubTab.Offers,
            label: PromotionHubTab.Offers,
            component: PromotionOffers,
        },
        promotionHubActiveQuickFilter: PromotionHubQuickFilter.NonStop,
    };
}

export default {
    namespaced: true,
    state: defaultState(),
    mutations: {
        [SET_AVAILABLE_PROMOTIONS]: createSimpleMutator<PromotionState, 'availablePromotions'>('availablePromotions'),
        [SET_IN_PROGRESS_PROMOTIONS]: createSimpleMutator<PromotionState, 'inProgressPromotions'>('inProgressPromotions'),
        [SET_HISTORY_PROMOTIONS]: createSimpleMutator<PromotionState, 'historyPromotions'>('historyPromotions'),
        [SET_EXPIRED_PROMOTIONS]: createSimpleMutator<PromotionState, 'expiredPromotions'>('expiredPromotions'),
        [SET_ARE_PROMOTIONS_FETCHED]: createSimpleMutator<PromotionState, 'arePromotionsFetched'>('arePromotionsFetched'),
        [SET_IS_PROMOTION_OPTIN_PROCESSING]: createSimpleMutator<PromotionState, 'isPromotionOptInProcessing'>('isPromotionOptInProcessing'),
        [SET_PROMOTION_HUB_ACTIVE_TAB]: createSimpleMutator<PromotionState, 'promotionHubActiveTab'>('promotionHubActiveTab'),
        [SET_PROMOTION_HUB_ACTIVE_QUICK_FILTER]: createSimpleMutator<PromotionState, 'promotionHubActiveQuickFilter'>('promotionHubActiveQuickFilter'),
    },
    actions: {
        async fetchPromotions({ commit, dispatch, rootGetters }: { commit: Commit, dispatch: any, rootGetters: any }) {
            const user = rootGetters['data/authentication/user'];
            let promotions: Promotion[] = [];
            if (user) {
                await dispatch(
                    'ui/promotionHub/bonuses/fetchBonuses', {
                        shouldUpdateBalance: true,
                        includeAwardingConditions: true,
                    }, { root: true });
                promotions = await getPlayerPromotions();
                await dispatch('savePromotionsToStore', promotions);
            } else {
                // Notice: We don't show promotions for non logged users on native apps for now so we need to hide it on web
                // this part won't be executed until we decide to show promotions for non logged users
                if (bonusProvider === BonusProvider.Comtrade) {
                    promotions = await getPromotions();
                    promotions = sortPromotions(promotions);
                    commit(SET_AVAILABLE_PROMOTIONS, adjustWelcomeBonusPromotions(promotions, bonusProvider));
                } else {
                    // TODO: waiting Betler to implement getPlayerPromotions endpoint for non logged users
                    promotions = [];
                }
                commit(SET_ARE_PROMOTIONS_FETCHED, true);
            }
        },
        async promotionOptIn({ commit, dispatch }: { commit: Commit, dispatch: any }, optInCode: string) {
            await errorGuard(async () => {
                commit(SET_IS_PROMOTION_OPTIN_PROCESSING, true);
                const promotions: Promotion[] = await promotionOptIn(optInCode);
                if (promotions.length === 0) {
                    commit(SET_IS_PROMOTION_OPTIN_PROCESSING, false);
                    return;
                }
                if (bonusProvider === BonusProvider.Comtrade) {
                    await dispatch(
                        'ui/promotionHub/bonuses/fetchBonuses', {
                            shouldUpdateBalance: true,
                            includeAwardingConditions: true,
                        }, { root: true });
                    await dispatch('savePromotionsToStore', promotions);
                } else {
                    dispatch('updateBetlerPromotion', promotions[0]);
                }
                commit(SET_IS_PROMOTION_OPTIN_PROCESSING, false);
            }, {
                onError: () => {
                    commit(SET_IS_PROMOTION_OPTIN_PROCESSING, false);
                },
            });
        },
        setArePromotionsFetched({ commit }: { commit: Commit }, value: boolean) {
            commit(SET_ARE_PROMOTIONS_FETCHED, value);
            if (!value) {
                commit(SET_AVAILABLE_PROMOTIONS, []);
            }
        },
        async savePromotionsToStore({ commit, dispatch, rootGetters, rootState }: { commit: Commit, dispatch: any, rootGetters: any, rootState: any }, fetchedPromotions: Promotion[]) {
            const promotions = bonusProvider === BonusProvider.Comtrade
                ? fillPromotionsWithBonusData(fetchedPromotions, rootGetters['ui/promotionHub/bonuses/pendingBonuses'])
                : fetchedPromotions;
            let { availablePromotions, inProgressPromotions, historyPromotions, expiredPromotions } = sortAndGroupPromotions(promotions);
            if (rootGetters['data/authentication/user'].welcomeBonus
                && getConfig().countrySpecific.user.bonus.isWelcomeBonusShown
            ) {
                if (!rootGetters['ui/user/bonuses/areWelcomeBonusesFetched']) {
                    await dispatch('ui/user/bonuses/fetchWelcomeBonuses', null, { root: true });
                }
                availablePromotions = adjustWelcomeBonusPromotions(
                    availablePromotions,
                    bonusProvider,
                    rootState.ui.user.bonuses.welcomeBonuses,
                );
            }

            commit(SET_ARE_PROMOTIONS_FETCHED, true);
            commit(SET_AVAILABLE_PROMOTIONS, availablePromotions);
            commit(SET_IN_PROGRESS_PROMOTIONS, inProgressPromotions);
            commit(SET_HISTORY_PROMOTIONS, historyPromotions);
            commit(SET_EXPIRED_PROMOTIONS, expiredPromotions);
        },
        updateBetlerPromotion({ commit, state }: { commit: Commit, state: PromotionState }, newPromotion: Promotion) {
            if (newPromotion.status === PromotionStatus.InProgress) {
                commit(SET_AVAILABLE_PROMOTIONS, state.availablePromotions.filter((promotion) => promotion.promotionId !== newPromotion.promotionId));
                const inProgressPromotions = state.inProgressPromotions;
                inProgressPromotions.push(newPromotion);
                commit(SET_IN_PROGRESS_PROMOTIONS, inProgressPromotions);
            }
        },
        async setActivePromotionHubTab({ commit }: { commit: Commit }, clickedTab: PromotionHubTab) {
            const promotionHubTabs = (await import('@src/app/promotionHub/shared/utils/promotionHubTabs')).promotionHubTabs;
            if (promotionHubTabs[clickedTab]) {
                commit(SET_PROMOTION_HUB_ACTIVE_TAB, {
                    value: promotionHubTabs[clickedTab].value,
                    label: promotionHubTabs[clickedTab].label,
                    component: promotionHubTabs[clickedTab].component,
                });
            }
        },
        setActivePromotionHubQuickFilter({ commit }: { commit: Commit }, quickFilter: PromotionHubQuickFilter) {
            commit(SET_PROMOTION_HUB_ACTIVE_QUICK_FILTER, quickFilter);
        },
    },
};
